import { useCallback, useContext } from "react";
import styles from "./looking.module.scss";
import { StateContext } from "../../../../../context/globalContext/context";
import { iGlobalContext } from "../../../../../context/globalContext/interface";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../../../../constant/google-analytics/GAEvents";
import { useTheresiasEventBridge } from "../../../../../services/intent/useTheresiasEventBridge";
import { lookingForIcon } from "../../../../../constant";
import Typography from "../../../../reusableComponent/typography/Typography";
import { Messages } from "../../../../../constant/messages/toastMessages";
import CustomChip from "../../../../reusableComponent/chip/Chip";

const lookingForData = [
  { id: 1, data: "Relaxing" },
  { id: 2, data: "Wellness" },
  { id: 3, data: "Thrill Seeker" },
  { id: 4, data: "Nature" },
  { id: 5, data: "Culture Curious" },
  { id: 6, data: "Foodie Frenzy" },
  { id: 7, data: "Party" },
  { id: 8, data: "Luxe" },
  { id: 9, data: "Romantic" },
  { id: 10, data: "City Buzz" },
  { id: 11, data: "Soul Searcher" },
  { id: 12, data: "Laptop & Latte" },
  { id: 13, data: "Local Secrets" },
];

interface Activity {
  id: number;
  data: string;
}

function LookingFor(): JSX.Element {
  const state = useContext(StateContext);
  const { onBoardForm, setOnBoardForm, isMobile }: iGlobalContext = state;
  const fireTheresiasEvent = useTheresiasEventBridge();
  const handleChipClick = useCallback(
    (activity: Activity) => () => {
      setOnBoardForm((prev: any) => {
        const current = prev.lookingFor as string[];
        const isSelected = current.includes(activity.data);
        // Compute the post-click selected array so the spec's `vibes`
        // field reflects state AFTER the toggle (not before).
        let nextVibes: string[];
        if (isSelected) nextVibes = current.filter((item) => item !== activity.data);
        else if (current.length >= 4) nextVibes = current;
        else nextVibes = [...current, activity.data];
        triggerGAEvent(GA_LABEL_ENUMS.onboarding_vibe, "", activity.data + "-Click");
        // Spec §F: event name embeds the slug of the toggled vibe; payload's
        // `vibes` is the FULL post-click array of selected vibes, each
        // UPPER_SNAKE_CASE (e.g. CITY_BUZZ | SOUL_SEARCHER | LAPTOP_LATTE).
        const toUpperSnake = (s: string) =>
          String(s || "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "");
        const vibeSlug = String(activity.data || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        fireTheresiasEvent(`onboarding-vibe-${vibeSlug || "unknown"}-click`, {
          vibes: nextVibes.map(toUpperSnake),
        });
        if (isSelected) {
          return {
            ...prev,
            lookingFor: current.filter((item) => item !== activity.data),
          };
        }

        if (current.length >= 4) {
          return prev;
        }

        return {
          ...prev,
          lookingFor: [...current, activity.data],
        };
      });
    },
    [setOnBoardForm, fireTheresiasEvent]
  );

  const selectedCount = onBoardForm?.lookingFor?.length;
  return (
    <div className={styles.step}>
      <div className={styles.imgContainer}>
        <img src={lookingForIcon} alt=""  width="113" height="111" /> 
        {/* className={styles.startIcon} */}
      </div>
      <Typography color="neutralDarker" weight={600} className={styles.title}>
        {Messages["onboarding.lookingFor"].message}
      </Typography>
      <Typography size="size14" color="brandPrimary" weight={400}>
        {selectedCount} / 4 Selected
      </Typography>
      {isMobile ? (
        <div className={styles.contentCard}>
          <div className={styles.chipCard}>
            {lookingForData.map((b) => (
              <CustomChip
                key={b.id}
                label={b.data}
                selected={(onBoardForm.lookingFor as string[]).includes(b.data)}
                onSelect={handleChipClick(b)}
                onBoarding={true}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className={styles.contentCard}>
          <div className={styles.chipRow4}>
            {lookingForData.slice(0, 3).map((b) => (
              <CustomChip
                key={b.id}
                label={b.data}
                selected={(onBoardForm.lookingFor as string[]).includes(b.data)}
                onSelect={handleChipClick(b)}
                onBoarding={true}
              />
            ))}
          </div>

          <div className={styles.chipRow5}>
            {lookingForData.slice(3, 6).map((b) => (
              <CustomChip
                key={b.id}
                label={b.data}
                selected={(onBoardForm.lookingFor as string[]).includes(b.data)}
                onSelect={handleChipClick(b)}
                onBoarding={true}
              />
            ))}
          </div>

          <div className={styles.chipRow4}>
            {lookingForData.slice(6, 10).map((b) => (
              <CustomChip
                key={b.id}
                label={b.data}
                selected={(onBoardForm.lookingFor as string[]).includes(b.data)}
                onSelect={handleChipClick(b)}
                onBoarding={true}
              />
            ))}
          </div>
          <div className={styles.chipRow5}>
            {lookingForData.slice(10, 13).map((b) => (
              <CustomChip
                key={b.id}
                label={b.data}
                selected={(onBoardForm.lookingFor as string[]).includes(b.data)}
                onSelect={handleChipClick(b)}
                onBoarding={true}
              />
            ))}
          </div>
        </div>
      )}

      <Typography weight={400} size="size14" className={styles.note}>
        {Messages["onboarding.preference.label"].message}
      </Typography>
    </div>
  );
}

export default LookingFor;
